import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, ExternalLink, Lock, Send, ShieldAlert } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useRefreshOnFocus } from "@/lib/use-refresh-on-focus";
import { markTicketNotificationsRead } from "@/lib/notifications";
import { formatPhone } from "@/lib/phone";
import { RatingPanel } from "@/components/tickets/RatingPanel";
import { AssigneePicker } from "@/components/tickets/AssigneePicker";
import { ChangeRequesterDialog } from "@/components/tickets/ChangeRequesterDialog";
import { ChangeSiteDialog } from "@/components/tickets/ChangeSiteDialog";
import { EscalatePanel } from "@/components/tickets/EscalatePanel";
import { RouteTicketDialog } from "@/components/tickets/RouteTicketDialog";
import { FollowUpPanel } from "@/components/tickets/FollowUpPanel";
import { SlaPanel } from "@/components/tickets/SlaPanel";
import { TicketActivityPanel } from "@/components/tickets/TicketActivityPanel";
import { TicketTabs, useRegisterTicketTab } from "@/components/tickets/TicketTabs";
import { AttachmentsPanel } from "@/components/AttachmentsPanel";
import { ParticipantsPanel } from "@/components/ParticipantsPanel";
import { WorkTimerPanel } from "@/components/WorkTimerPanel";
import { CannedResponsePicker } from "@/components/CannedResponsePicker";
import { Avatar } from "@/components/Avatar";
import { PriorityPill } from "@/components/PriorityPill";
import { useSession, useZone } from "@/components/session-provider";
import { formatDate } from "@/lib/time";
import {
  addComment,
  ageOf,
  changePriority,
  changeStatus,
  getComments,
  getTicket,
  statusActionLabel,
  statusActionTone,
  statusLabel,
  statusTone,
  type TicketComment,
  type TicketDetail,
  type TicketPriority,
  type TicketStatus,
} from "@/lib/tickets";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/tickets/$id")({
  component: TicketDetailPage,
});

const priorities: TicketPriority[] = ["Low", "Normal", "High", "Urgent", "Critical"];

function TicketDetailPage() {
  const { id } = Route.useParams();

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [comments, setComments] = useState<TicketComment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  /**
   * @param quiet A background re-read, rather than one somebody is waiting on.
   *
   * ⚠️ A quiet failure is swallowed. This runs when somebody returns to the tab, so a blip
   * on the way back would otherwise paint an error banner over a page that is fine and has
   * been readable the whole time — the same reasoning as `SessionProvider.load`.
   */
  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setError(null);

      try {
        // Both in parallel. The thread is the slower of the two and there is no reason to
        // wait for the header before starting it.
        const [detail, thread] = await Promise.all([getTicket(id), getComments(id)]);
        setTicket(detail);
        setComments(thread);
        setError(null);
      } catch (e) {
        // 404 covers both "no such ticket" and "not yours to see" — deliberately
        // indistinguishable, so the status code is not an enumeration oracle. The screen
        // says the same thing for both, because it genuinely does not know which it is.
        //
        // ⚠️ Applied on a quiet pass too. A request that has moved out of reach while the
        // tab was in the background — reclassified, or a team membership withdrawn — must
        // stop being readable here, not go on rendering because nobody was watching.
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
          return;
        }

        if (!quiet) setError(e instanceof ApiError ? e.message : "Could not load this ticket.");
      }
    },
    [id],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // ⚠️ THE THREAD WENT STALE AND NOTHING SAID SO. This screen fetched once and never again:
  // two agents working one request never saw each other's replies, and somebody who switched
  // to Outlook to find the error message they had been asked for came back to the page they
  // left — including its status, assignee and priority, any of which a colleague may have
  // moved in between. The queue at least had a Refresh button; the busiest screen in the
  // product had nothing.
  //
  // ⚠️ EACH REFRESH RECORDS A PHI READ, and that is correct rather than a side effect worth
  // avoiding. The record is being re-fetched and re-displayed, so §164.312(b) says the ledger
  // should carry it — and the alternative, refreshing only the comment thread because that
  // endpoint happens not to audit, would be choosing an endpoint specifically to route around
  // the audit trail. Volume in the ledger is not a reason to under-record a read.
  useRefreshOnFocus(() => void load(true), !notFound);

  // ⚠️ Reading a request clears its unread badge, and this is the only thing that does.
  // Before it, somebody could read every reply here, go back to the home screen and still be
  // told there were three updates waiting — a badge that outlives the act of reading, which
  // teaches people to ignore it.
  //
  // ⚠️ Fired on a SUCCESSFUL load, not on navigation. A 404 — which covers both "no such
  // ticket" and "not yours to see" — must not mark anything, and the server would refuse it
  // anyway: the notification filter joins through Tickets.
  //
  // ⚠️ Failure is swallowed. This is housekeeping on a page that has already rendered, and an
  // error toast over a request somebody is reading would be worse than a badge that clears on
  // their next visit instead of this one.
  useEffect(() => {
    if (!ticket) return;

    void markTicketNotificationsRead(ticket.number).catch(() => undefined);
  }, [ticket?.number]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only after the server has served it. A tab for a ticket the caller cannot open would
  // be a list of ticket numbers they are not entitled to — the opposite of the point.
  useRegisterTicketTab(
    ticket
      ? { number: ticket.number, displayNumber: ticket.displayNumber, subject: ticket.subject }
      : null,
  );

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" aria-hidden />
        <h1 className="mt-4 text-xl font-semibold">Ticket not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have been deleted, or you may not have access to it.
        </p>
        <Link
          to="/tickets"
          className="mt-6 inline-flex h-10 items-center gap-2 rounded-lg border border-input px-4 text-sm transition hover:bg-accent"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back to tickets
        </Link>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6" aria-busy="true">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="mt-4 h-40 animate-pulse rounded-2xl border border-border bg-card" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
      <TicketTabs activeNumber={ticket.number} />

      <Link
        to="/tickets"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Tickets
      </Link>

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
        >
          {error}
        </div>
      )}

      <header className="mt-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* The readable number leads, because it is what people say to each other. The
              opaque one stays visible beside it — it is what addresses the ticket, and
              what someone pastes when a link matters. */}
          <span className="text-sm font-semibold">#{ticket.displayNumber}</span>
          <span className="font-mono text-xs text-muted-foreground">{ticket.number}</span>
          <span
            className={cn("rounded-md px-2 py-0.5 text-xs font-medium", statusTone[ticket.status])}
          >
            {statusLabel[ticket.status]}
          </span>
          <PriorityPill p={ticket.priority} />
          {ticket.sensitivity !== "Standard" && (
            <span className="inline-flex items-center gap-1 rounded-md bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning">
              <Lock className="h-3 w-3" aria-hidden /> {ticket.sensitivity}
            </span>
          )}
        </div>

        <h1 className="mt-2 text-xl font-semibold tracking-tight sm:text-2xl">{ticket.subject}</h1>
      </header>

      {/* Single column on a phone; detail rail beside the thread from lg up. */}
      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="min-w-0">
          {/* Moved above the description: inviting someone is something you do at the
              START of working a request, not after scrolling past its history. */}
          <ParticipantsPanel ticketNumber={ticket.number} />

          <section className="mt-4 rounded-2xl border border-border bg-card p-4">
            {/* Labelled rather than left to inference. The subject sits in the page
                heading and the description below it, and without labels the two read as
                one block of text — which matters most on the screens where somebody is
                skim-reading a request they did not write. */}
            <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Subject
            </h2>
            <p className="mt-1 text-sm font-medium leading-relaxed">{ticket.subject}</p>

            <h3 className="mt-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Description
            </h3>
            {/* Plain text. Rendering user-supplied HTML here is what
                dangerouslySetInnerHTML is banned for; whitespace-pre-wrap keeps the
                formatting a person typed without interpreting any of it. */}
            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{ticket.description}</p>
          </section>

          {/* No canUpload prop: the server folds the feature flag, the permission, the
              ticket's status and whether storage exists into one answer it also
              enforces. Deciding it here would be a second, drifting copy of the rule. */}
          <AttachmentsPanel ticketNumber={ticket.number} />

          {/* Requester only, and only once closed (D-24). The server decides both. */}
          {ticket.canRate && (
            <div className="mt-4">
              <RatingPanel ticketNumber={ticket.number} />
            </div>
          )}

          <Thread
            comments={comments}
            ticket={ticket}
            onPosted={(comment) => setComments((c) => [...(c ?? []), comment])}
          />

          {/* Renders nothing unless the caller holds audit.read — the panel asks and
              stays hidden on a 403, and the endpoint enforces it regardless. */}
          <TicketActivityPanel ticketNumber={ticket.number} />
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          {/* Above the details, because "when did we say we'd reply" is the question an
              agent picking this up asks first. Renders nothing when no policy applied. */}
          <SlaPanel ticket={ticket} />

          {/* ⚠️ In the rail rather than tucked into an action menu, and deliberately so.
              This is the one feature that records what a named employee was doing minute by
              minute; a measurement somebody cannot see being taken is surveillance rather
              than time tracking, so the running state and their own total stay in view. */}
          <WorkTimerPanel ticketNumber={ticket.number} canRecord={ticket.canRecordWork} />

          {/* ⚠️ Beside the timer rather than in the action menu, because the moment it is for
              is the moment an agent is already looking at this rail: they have just told
              somebody "I'll check on Monday", and the reminder has to be one click away from
              that thought or it is never set at all.

              ⚠️ Shows the READER'S OWN follow-ups and nobody else's, at any permission level.
              `canRecordWork` is ticket.update on this record — the same gate the timer uses,
              and the reason a requester has neither. */}
          <FollowUpPanel
            ticketNumber={ticket.number}
            displayNumber={ticket.displayNumber}
            subject={ticket.subject}
            canRecord={ticket.canRecordWork}
          />

          <DetailPanel ticket={ticket} onChanged={setTicket} onError={setError} />

          {/* Below the details and above the agent actions: a requester scrolling this
              column is looking for "how do I chase this", and an agent reading the same
              column wants to see it has been chased before they decide what to do next. */}
          <EscalatePanel ticket={ticket} onChanged={setTicket} />

          <ActionPanel ticket={ticket} onChanged={setTicket} onError={setError} />
        </aside>
      </div>
    </div>
  );
}

/**
 * Who and what this request is, and the two fields somebody changes most often.
 *
 * ⚠️ Assignment and priority are edited ON the field that states them, rather than in a
 * separate list of actions further down the rail. They were in both places conceptually
 * and neither physically: the panel said "Assignee: Unassigned" and the control that
 * changed it sat in a different box under a different heading, so the answer and the way to
 * correct it never appeared together.
 *
 * Both controls still decide nothing. `canAssign` and `canChangePriority` come from the
 * server and govern rendering only; each endpoint re-checks the caller against this
 * specific record, so an edited flag yields a control that 404s.
 */
function DetailPanel({
  ticket,
  onChanged,
  onError,
}: {
  ticket: TicketDetail;
  onChanged: (t: TicketDetail) => void;
  onError: (message: string | null) => void;
}) {
  const zone = useZone();

  const [busy, setBusy] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [routing, setRouting] = useState(false);
  const [changingSite, setChangingSite] = useState(false);

  async function run(action: () => Promise<TicketDetail>) {
    setBusy(true);
    onError(null);

    try {
      onChanged(await action());
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "That change could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  const rows: [string, string][] = [
    // ⚠️ Team is drawn OUTSIDE this list since D-96, because it is the one row here that now
    // carries a control. Leaving it in and special-casing it inside the loop would have made the
    // loop know about routing.
    ["Category", ticket.categoryName],

    // Omitted rather than shown empty on requests raised before this was collected: a
    // "Contact — —" row invites somebody to conclude the number failed to save.
    ...(ticket.contactPhone
      ? ([["Contact", formatPhone(ticket.contactPhone, ticket.contactPhoneExtension)]] as [
          string,
          string,
        ][])
      : []),

    ["Opened", `${ageOf(ticket.createdAtUtc)} ago`],
  ];

  return (
    <section className="rounded-2xl border border-border bg-card p-4">
      <h2 className="text-sm font-medium">Details</h2>

      <div className="mt-3 space-y-3 text-sm">
        <Field label="Requester">
          <span className="flex min-w-0 items-center gap-2">
            <RequesterName ticket={ticket} />

            {/* ⚠️ Drawn beside the name it corrects, for the same reason assignment and
                priority are edited on the field that states them: the answer and the way to
                fix it belong in one place. Rendering only — `canChangeRequester` is its own
                permission and the endpoint re-checks against this record. */}
            {ticket.canChangeRequester && (
              <button
                type="button"
                onClick={() => setCorrecting(true)}
                disabled={busy}
                className="shrink-0 rounded-md border border-input px-1.5 py-0.5 text-xs text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:opacity-50"
              >
                Correct
              </button>
            )}
          </span>
        </Field>

        {correcting && (
          <ChangeRequesterDialog
            ticket={ticket}
            onClose={() => setCorrecting(false)}
            onChanged={onChanged}
          />
        )}

        {/* ⚠️ Team, with the control beside the field that states it (D-96) — the same reasoning
            as the requester correction above and as assignment and priority: the answer and the
            way to change it belong in one place. Rendering only; `canRoute` is its own
            permission and the endpoint re-checks against this record. */}
        <Field label="Team">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{ticket.teamName}</span>

            {ticket.canRoute && (
              <button
                type="button"
                onClick={() => setRouting(true)}
                disabled={busy}
                className="shrink-0 rounded-md border border-input px-1.5 py-0.5 text-xs text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:opacity-50"
              >
                Pass on
              </button>
            )}
          </span>
        </Field>

        {routing && (
          <RouteTicketDialog
            ticket={ticket}
            onClose={() => setRouting(false)}
            onRouted={onChanged}
          />
        )}

        {/* ⚠️ One site row, not two (D-82). "Requester's site" sat beside this one and D-43
            had already made it redundant — a ticket's site IS derived from the requester's
            placement whenever they have one — so the pair only raised the question of which
            was correct.

            ⚠️ It moved out of the plain list below and gained a control, for the reason the
            requester and the team above it have one: a site is mandatory now (D-100), so the
            answer given once on the form is the answer for ever unless this exists. Rendering
            only — `canChangeSite` is `ticket.update` and the endpoint re-checks this record. */}
        <Field label="Site">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{ticket.siteName ?? "—"}</span>

            {ticket.canChangeSite && (
              <button
                type="button"
                onClick={() => setChangingSite(true)}
                disabled={busy}
                className="shrink-0 rounded-md border border-input px-1.5 py-0.5 text-xs text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:opacity-50"
              >
                Correct
              </button>
            )}
          </span>
        </Field>

        {changingSite && (
          <ChangeSiteDialog
            ticket={ticket}
            onClose={() => setChangingSite(false)}
            onChanged={onChanged}
          />
        )}

        {/* ⚠️ Only present when somebody else filed it, and then it is not optional detail.
            Without this the record reads as though the requester wrote it — which would be
            the system stating something untrue about a named person. */}
        {ticket.submittedByName && (
          <Field label="Submitted by">
            <span className="text-sm">{ticket.submittedByName}</span>
          </Field>
        )}

        <Field label="Assignee" htmlFor={ticket.canAssign ? "assignee" : undefined}>
          {ticket.canAssign ? (
            <AssigneePicker
              ticket={ticket}
              disabled={busy}
              onChanged={onChanged}
              onError={onError}
            />
          ) : ticket.assigneeName && ticket.assigneeUserId ? (
            // A face here is the whole point of D-38 for a requester: they cannot open the
            // assignee picker and never could, so without this the person looking after
            // their request was a name and nothing else.
            <span className="inline-flex max-w-full items-center gap-2 text-sm">
              <Avatar
                userId={ticket.assigneeUserId}
                name={ticket.assigneeName}
                hasPicture={ticket.assigneeHasAvatar}
                size="sm"
              />
              <span className="truncate">{ticket.assigneeName}</span>
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">Unassigned</span>
          )}
        </Field>

        <Field label="Priority" htmlFor={ticket.canChangePriority ? "priority" : undefined}>
          {ticket.canChangePriority ? (
            <select
              id="priority"
              value={ticket.priority}
              disabled={busy}
              onChange={(e) =>
                void run(() => changePriority(ticket.number, e.target.value as TicketPriority))
              }
              className="h-9 w-full rounded-lg border border-input bg-background px-2 text-base transition focus:border-ring focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            >
              {priorities.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          ) : (
            <PriorityPill p={ticket.priority} />
          )}
        </Field>
      </div>

      <dl className="mt-4 space-y-2 border-t border-border pt-3 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-start justify-between gap-3">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="text-right">{value}</dd>
          </div>
        ))}
      </dl>

      {ticket.onHoldUntilUtc && (
        <p className="mt-3 rounded-md bg-muted px-2.5 py-2 text-xs text-muted-foreground">
          On hold until {formatDate(ticket.onHoldUntilUtc, zone)}
          {ticket.onHoldReason ? ` — ${ticket.onHoldReason}` : ""}
        </p>
      )}
    </section>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">
          {label}
        </label>
      ) : (
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      )}
      <div className="mt-1">{children}</div>
    </div>
  );
}

/**
 * The requester, as a link to their record where that is possible.
 *
 * ⚠️ Everybody now sees the FACE; only `user.manage` gets the LINK (D-38). The id used to
 * arrive solely for callers who could follow it, so withholding it did both jobs at once.
 * Faces are for everyone — the person who raised the request should see who they are
 * dealing with — and `canViewPerson` carries the permission question on its own.
 *
 * The name-only branch is the ordinary case, not an error case, and must not look like one:
 * no underline, no disabled-link styling, nothing suggesting a control that is not working.
 */
function RequesterName({ ticket }: { ticket: TicketDetail }) {
  const face = (
    <Avatar
      userId={ticket.requesterUserId}
      name={ticket.requesterName}
      hasPicture={ticket.requesterHasAvatar}
      size="sm"
    />
  );

  if (!ticket.canViewPerson) {
    return (
      <span className="inline-flex max-w-full items-center gap-2 text-sm">
        {face}
        <span className="truncate">{ticket.requesterName}</span>
      </span>
    );
  }

  return (
    <Link
      to="/people/$id"
      params={{ id: ticket.requesterUserId }}
      className="group inline-flex max-w-full items-center gap-2 rounded-lg border border-transparent px-1.5 py-1 -mx-1.5 text-sm transition hover:border-border hover:bg-accent/50"
    >
      {face}
      <span className="truncate font-medium group-hover:text-primary">{ticket.requesterName}</span>
      <ExternalLink
        className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
        aria-hidden
      />
    </Link>
  );
}

/**
 * Where this request goes next.
 *
 * Assignment and priority used to live here too and have moved onto the fields they
 * describe, which leaves this panel doing one thing: the lifecycle.
 */
function ActionPanel({
  ticket,
  onChanged,
  onError,
}: {
  ticket: TicketDetail;
  onChanged: (t: TicketDetail) => void;
  onError: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);

  // Nothing to draw when the caller may not move it. The server would refuse anyway; this
  // only avoids offering a control that always fails.
  if (!ticket.canChangeStatus || ticket.allowedTransitions.length === 0) return null;

  async function run(action: () => Promise<TicketDetail>) {
    setBusy(true);
    onError(null);

    try {
      onChanged(await action());
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "That change could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-4">
      <h2 className="text-sm font-medium">Move this request</h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Currently {statusLabel[ticket.status].toLowerCase()}.
      </p>

      {/* Only the transitions the server says are legal from here. Offering every status
          and learning the rule from a 409 is a worse way to find out.

          ⚠️ Coloured by MEANING rather than by position in the list: resolving is always
          the green one and reopening is always the outlined one, whichever order the server
          returns them in. A button whose colour depends on where it landed teaches nothing
          and eventually teaches something wrong. */}
      <div className="mt-3 flex flex-wrap gap-2">
        {ticket.allowedTransitions.map((next) => (
          <button
            key={next}
            type="button"
            disabled={busy}
            onClick={() => void run(() => changeStatus(ticket.number, next as TicketStatus))}
            className={cn(
              "inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
              statusActionTone[next as TicketStatus],
            )}
          >
            {next === "Resolved" && <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />}
            {statusActionLabel(ticket.status, next as TicketStatus)}
          </button>
        ))}
      </div>
    </section>
  );
}

function Thread({
  comments,
  ticket,
  onPosted,
}: {
  comments: TicketComment[] | null;
  ticket: TicketDetail;
  onPosted: (comment: TicketComment) => void;
}) {
  const { user } = useSession();

  // The picker substitutes {{agent_name}} with this. Their own name, already on screen.
  const currentUserName = user?.displayName ?? "";

  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // What may be written here, mirroring the server's rule exactly (D-61).
  //
  // A closed request takes no REPLY — it is finished and nobody is watching it — but it
  // still takes an internal note, because the desk documenting a closure must not have to
  // reopen somebody's request to do it. The requester never sees a note (D2).
  //
  // ⚠️ This was `ticket.isOpen`, which is false for Resolved as well as Closed, so the
  // composer also vanished on a resolved request — while the endpoint deliberately treats a
  // requester's reply there as "this is not fixed" and reopens it. The one screen where that
  // sentence gets typed was the one place it could not be. The rule is the status, not
  // openness.
  //
  // Rendering only: the endpoint refuses a public reply on a closed request with a 409
  // whatever this file draws.
  const closed = ticket.status === "Closed";
  const mayReply = ticket.canComment && !closed;
  const mayNote = ticket.canCommentInternal;

  // Forced rather than defaulted. On a closed request an internal note is the only thing
  // that can be posted, so the box must not be able to hold a reply it will send as one —
  // and the toggle below is replaced by a statement, not left switchable.
  const isInternal = closed ? true : internal;

  // Takes anything cancellable rather than a FormEvent specifically, so the keyboard
  // shortcut on the textarea can reach the same one path. Two submit paths that drift is
  // how one of them ends up skipping a check the other makes.
  async function submit(e: { preventDefault: () => void }) {
    e.preventDefault();
    if (!body.trim() || busy) return;

    setBusy(true);
    setError(null);

    try {
      onPosted(await addComment(ticket.number, body.trim(), isInternal));
      setBody("");
      setInternal(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Your reply was not saved.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-4">
      <h2 className="text-sm font-medium">Activity</h2>

      {comments === null ? (
        <div className="mt-3 h-24 animate-pulse rounded-2xl border border-border bg-card" />
      ) : comments.length === 0 ? (
        <p className="mt-3 rounded-2xl border border-dashed border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
          No replies yet.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {comments.map((c) => (
            <li
              key={c.id}
              className={cn(
                "rounded-2xl border p-3",
                c.kind === "InternalNote"
                  ? "border-warning/40 bg-warning/5"
                  : "border-border bg-card",
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                {/* ⚠️ The RING carries the role, not the fill, and that is deliberate now
                    that a real photograph can occupy the circle (D-38). Every avatar was
                    once the same accent colour, so on a long thread nothing distinguished
                    the person who raised the request from the people answering it. The role
                    comes from the server (authorKind), never from comparing display names,
                    which breaks the moment two colleagues share one — and it is named in
                    text beside this, because colour alone fails a colour-blind reader. */}
                <Avatar
                  userId={c.authorUserId}
                  name={c.authorName}
                  hasPicture={c.authorHasAvatar}
                  size="sm"
                  className={cn(
                    "ring-1",
                    c.authorKind === "Requester"
                      ? "bg-sky-500/15 text-sky-600 ring-sky-500/30 dark:text-sky-400"
                      : c.authorKind === "System"
                        ? "bg-muted text-muted-foreground ring-border"
                        : "bg-teal-500/15 text-teal-700 ring-teal-500/30 dark:text-teal-400",
                  )}
                />
                <span className="text-sm font-medium">{c.authorName}</span>

                {/* Named, not just coloured. Colour alone fails for a colour-blind reader
                    and disappears in a high-contrast theme. */}
                {c.authorKind !== "System" && (
                  <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {c.authorKind === "Requester" ? "Requester" : "Agent"}
                  </span>
                )}

                {c.kind === "InternalNote" && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning">
                    <Lock className="h-2.5 w-2.5" aria-hidden /> Internal
                  </span>
                )}

                <span className="ml-auto text-xs text-muted-foreground">
                  {ageOf(c.createdAtUtc)} ago
                </span>
              </div>

              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{c.body}</p>
            </li>
          ))}
        </ul>
      )}

      {(mayReply || mayNote) && (
        <form
          onSubmit={submit}
          className={cn(
            "mt-4 rounded-2xl border p-3 transition-colors",
            // ⚠️ The whole composer changes colour for an internal note, not just the
            // checkbox beside it. Posting an internal note where a reply was meant is
            // invisible to the writer and permanent to the record — and the reverse, a
            // note about a request written where the requester can read it, is worse. The
            // control that decides which is a checkbox two inches below the cursor, so the
            // state it selects is stated where the typing happens.
            isInternal ? "border-warning/40 bg-warning/5" : "border-border bg-card",
          )}
        >
          {/* Said once, above the box, where somebody about to type is looking. "Closed" is
              already on the pill at the top of the page; what is not obvious is that a reply
              is refused and a note is not. */}
          {closed && (
            <p className="mb-2 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
              <span>
                This request is closed, so it takes no reply — reopen it to answer the requester. An
                internal note is still recorded against it.
              </span>
            </p>
          )}

          <label htmlFor="reply" className="sr-only">
            {isInternal ? "Internal note" : "Reply"}
          </label>
          <textarea
            id="reply"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              // Ctrl/Cmd+Enter sends, as every other message box does. Enter alone must
              // not: these are multi-paragraph replies about somebody's request, and a
              // half-written one posted by a stray keystroke cannot be taken back.
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void submit(e);
            }}
            // Six rows rather than three. A reply on a service desk is a paragraph or two,
            // and a box that shows three lines of it makes people write less than they
            // meant to and re-read what they wrote by scrolling.
            rows={6}
            placeholder={
              isInternal ? "Internal note — the requester will never see this" : "Write a reply…"
            }
            className="min-h-32 w-full resize-y rounded-lg border border-input bg-background p-3 text-base leading-relaxed transition focus:border-ring focus:outline-none md:text-sm"
          />

          {error && (
            <p role="alert" className="mt-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-3">
            {/* Fills the box above; never sends. The agent edits it and posts it under
                their own name — these are written by an administrator and used by
                somebody else, so a one-click send would put words in their mouth.

                Agents only. A requester replying on their own request was being offered
                the service desk's internal phrasing, which is a control that belongs to
                somebody else's job. The endpoint refuses them too. */}
            {ticket.canUseCannedResponses && (
              <CannedResponsePicker
                // The opaque number here — this one addresses the ticket, and it is what
                // the endpoint is called with. The readable one goes into the words the
                // requester reads (D-35). The two are deliberately not the same prop.
                ticketNumber={ticket.number}
                context={{
                  requesterName: ticket.requesterName,
                  displayNumber: ticket.displayNumber,
                  agentName: currentUserName,
                }}
                onInsert={(text) =>
                  setBody((current) => (current ? `${current}\n\n${text}` : text))
                }
                // ⚠️ So a reply worth reusing can be saved from where it was written (D-94).
                // Passed rather than re-read from the DOM, and the picker saves it verbatim —
                // the panel says so before anybody presses, because a sentence naming this
                // caller becomes a template destined for somebody else's request.
                draft={body}
              />
            )}

            {/* Only offered to callers the server says may write one. Posting is
                re-checked there regardless — this checkbox decides nothing.

                ⚠️ A STATEMENT rather than a checkbox on a closed request: there is one thing
                that can be posted, so a control offering a choice between two would be
                offering one that gets refused. A disabled checked box says the same thing less
                clearly. */}
            {mayNote && closed && (
              <span className="inline-flex h-9 items-center gap-2 rounded-lg border border-warning/50 bg-warning/15 px-2.5 text-sm font-medium text-warning">
                <Lock className="h-3 w-3" aria-hidden />
                Internal note
              </span>
            )}

            {mayNote && !closed && (
              <label
                className={cn(
                  "inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border px-2.5 text-sm transition",
                  internal
                    ? "border-warning/50 bg-warning/15 font-medium text-warning"
                    : "border-input text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                <input
                  type="checkbox"
                  checked={internal}
                  onChange={(e) => setInternal(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                <Lock className="h-3 w-3" aria-hidden />
                Internal note
              </label>
            )}

            <button
              type="submit"
              disabled={busy || !body.trim()}
              className={cn(
                "ml-auto inline-flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-medium transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50",
                // The send button carries the same state as the composer around it, so the
                // last thing under the cursor before posting says which of the two this is.
                isInternal
                  ? "bg-warning text-warning-foreground"
                  : "bg-gradient-primary text-primary-foreground shadow-glow",
              )}
            >
              <Send className="h-3.5 w-3.5" aria-hidden />
              {busy ? "Sending…" : isInternal ? "Add note" : "Reply"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
